import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'
import { CertificationInlineRich } from '@components/certification/CertificationRichText'
import { formatUserAnswer, formatCorrectAnswer } from '@lib/certificationGrade'

const HOVER_CLOSE_MS = 220

function useFinePointerHover() {
  const [supports, setSupports] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)')
    const apply = () => setSupports(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])
  return supports
}

function clampPanelPosition(anchorRect) {
  if (typeof window === 'undefined') {
    return { left: 16, top: 80, width: 400, maxHeight: 320 }
  }
  const pad = 12
  const vw = window.innerWidth
  const vh = window.innerHeight
  const width = Math.min(480, vw - pad * 2)
  const maxHeight = Math.min(vh * 0.55, 420)
  let top = anchorRect.bottom + 8
  if (top + maxHeight + pad > vh) {
    top = anchorRect.top - maxHeight - 8
  }
  if (top < pad) top = pad
  let left = anchorRect.left + (anchorRect.width - width) / 2
  left = Math.max(pad, Math.min(left, vw - width - pad))
  return { left, top, width, maxHeight }
}

export default function WrongAnswerReviewList({ wrong }) {
  const panelDomId = `wrong-answer-review-panel-${useId().replace(/:/g, '')}`
  const supportsHover = useFinePointerHover()
  const [pinnedId, setPinnedId] = useState(null)
  const [ephemeralId, setEphemeralId] = useState(null)
  const [pos, setPos] = useState({ left: 0, top: 0, width: 400, maxHeight: 320 })
  const hoverCloseTimer = useRef(null)
  const triggerRefs = useRef(new Map())
  const panelRef = useRef(null)

  const displayId = pinnedId ?? ephemeralId
  const activeItem = displayId != null ? wrong.find((w) => w.question.id === displayId) : null

  const clearHoverTimer = useCallback(() => {
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current)
      hoverCloseTimer.current = null
    }
  }, [])

  const scheduleClearEphemeral = useCallback(() => {
    if (pinnedId != null) return
    clearHoverTimer()
    hoverCloseTimer.current = window.setTimeout(() => {
      setEphemeralId(null)
      hoverCloseTimer.current = null
    }, HOVER_CLOSE_MS)
  }, [pinnedId, clearHoverTimer])

  const updatePosition = useCallback((id) => {
    const el = triggerRefs.current.get(id)
    if (!el || typeof window === 'undefined') return
    const r = el.getBoundingClientRect()
    setPos(clampPanelPosition(r))
  }, [])

  useLayoutEffect(() => {
    if (displayId == null) return
    updatePosition(displayId)
    const onResize = () => updatePosition(displayId)
    const onScroll = () => updatePosition(displayId)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [displayId, updatePosition])

  useEffect(() => {
    if (displayId == null) return
    const id = displayId
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPinnedId(null)
        setEphemeralId(null)
        window.requestAnimationFrame(() => triggerRefs.current.get(id)?.focus())
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [displayId])

  useEffect(() => {
    if (pinnedId == null) return
    const onPointerDown = (e) => {
      const panel = panelRef.current
      const t = e.target
      if (panel?.contains(t)) return
      for (const el of triggerRefs.current.values()) {
        if (el?.contains(t)) return
      }
      setPinnedId(null)
      setEphemeralId(null)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [pinnedId])

  const onRowMouseEnter = (id) => {
    if (!supportsHover) return
    clearHoverTimer()
    setEphemeralId(id)
    updatePosition(id)
  }

  const onRowMouseLeave = () => {
    if (!supportsHover) return
    scheduleClearEphemeral()
  }

  const onPanelMouseEnter = () => {
    clearHoverTimer()
  }

  const onPanelMouseLeave = () => {
    scheduleClearEphemeral()
  }

  const onRowClick = (id) => {
    clearHoverTimer()
    if (pinnedId === id) {
      setPinnedId(null)
      setEphemeralId(null)
      return
    }
    setPinnedId(id)
    setEphemeralId(null)
    updatePosition(id)
  }

  const onTriggerFocus = (id) => {
    clearHoverTimer()
    if (pinnedId != null) return
    setEphemeralId(id)
    updatePosition(id)
  }

  const onTriggerBlur = () => {
    window.setTimeout(() => {
      const panel = panelRef.current
      const ae = document.activeElement
      if (panel?.contains(ae)) return
      if (pinnedId != null) return
      for (const el of triggerRefs.current.values()) {
        if (el?.contains(ae)) return
      }
      setEphemeralId(null)
    }, 0)
  }

  const setTriggerRef = (id, el) => {
    if (el) triggerRefs.current.set(id, el)
    else triggerRefs.current.delete(id)
  }

  const portal =
    activeItem && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={panelDomId}
            role="region"
            aria-label={`Details for question ${activeItem.question.id}`}
            className="fixed z-[70] rounded-xl border border-primary-500/35 bg-navy-950/95 shadow-2xl backdrop-blur-md p-4 sm:p-5 text-left overflow-y-auto print:hidden"
            style={{
              left: pos.left,
              top: pos.top,
              width: pos.width,
              maxHeight: pos.maxHeight
            }}
            onMouseEnter={onPanelMouseEnter}
            onMouseLeave={onPanelMouseLeave}
          >
            <p className="text-xs text-primary-400 font-semibold mb-2">
              Question {activeItem.question.id} · {activeItem.question.points ?? (activeItem.question.id <= 20 ? 2 : 3)}{' '}
              pts if correct
            </p>
            <div className="text-sm text-neutral-200 mb-3 whitespace-pre-wrap leading-relaxed">
              <CertificationInlineRich text={activeItem.question.stem} strongClassName="font-semibold text-primary-200" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <span className="text-neutral-500 block text-xs uppercase tracking-wide mb-1">Your answer</span>
                <span className="text-neutral-200">{formatUserAnswer(activeItem.question, activeItem.userValue)}</span>
              </div>
              <div>
                <span className="text-neutral-500 block text-xs uppercase tracking-wide mb-1">Correct answer</span>
                <span className="text-primary-300 font-medium">{formatCorrectAnswer(activeItem.question)}</span>
              </div>
            </div>
            <div className="text-sm text-neutral-400 italic leading-relaxed">
              <CertificationInlineRich text={activeItem.question.concept} strongClassName="font-semibold text-neutral-300" />
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <>
      <ul className="space-y-2">
        {wrong.map(({ question, userValue }) => {
          const id = question.id
          const isOpen = displayId === id
          const pts = question.points ?? (id <= 20 ? 2 : 3)
          return (
            <li key={id}>
              <button
                type="button"
                ref={(el) => setTriggerRef(id, el)}
                aria-expanded={isOpen}
                aria-controls={panelDomId}
                className={clsx(
                  'w-full text-left rounded-lg border px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/70',
                  isOpen
                    ? 'border-primary-500/50 bg-primary-500/10 text-white'
                    : 'border-white/10 bg-white/[0.03] text-neutral-200 hover:border-primary-500/30 hover:bg-white/[0.06]'
                )}
                onClick={() => onRowClick(id)}
                onMouseEnter={() => onRowMouseEnter(id)}
                onMouseLeave={onRowMouseLeave}
                onFocus={() => onTriggerFocus(id)}
                onBlur={onTriggerBlur}
              >
                <span className="text-sm font-medium text-white">
                  Question {id} · {pts} pts · Incorrect
                </span>
                <span className="block text-xs text-neutral-500 mt-1">
                  {supportsHover ? 'Hover for details, or click to pin' : 'Tap for details'}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      {portal}
    </>
  )
}
